/**
 * components/Onboarding/OnboardingWizard.tsx
 * Modern wizard-based onboarding flow
 * 
 * Provides a professional, step-by-step onboarding experience with:
 * - Dynamic step progression based on user choices
 * - HeroUI Form integration with automatic validation
 * - Progress tracking and visual feedback
 * - Modal escape hatches for external features
 * - Responsive design for all screen sizes
 */

// 1. External dependencies
import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// 2. Internal absolute imports
import { ScrollShadowRef, useToastNotification } from '@enfomentis/ui';
import { WizardLayout } from '@/components/shared/layouts';
import { AppSyncStatus } from '@/components/shared/sync';
import { SupportButton } from '@/featurebase';
import { userService } from '@/services/UserService';
import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';
import { createProvider } from '@/services/sync/providerFactory';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { useAuth } from '@/hooks/useAuth';
import { useUserSyncSettings, useSyncConnection } from '@/contexts';
import { softDeleteRecord } from '@/services/sync/SoftDeleteUtils';
import { Brand } from '@enfomentis/shared/config';
import type { ProviderKey, SyncProviderType } from '@/types/sync';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';

// 3. Relative imports
import { useWizardNavigation } from './hooks/useWizardNavigation';
import { useOnboardingData } from './hooks/useOnboardingData';
import { persistOnboardingData } from './services/persistOnboardingData';
import { WelcomeStep } from './steps/WelcomeStep';
import { StorageProviderStep } from './steps/StorageProviderStep';
import { AccountSetupIntroduction } from './steps/AccountSetupIntroduction';
import { StrategyIntroduction } from './steps/StrategyIntroduction';
import { RiskIntroduction } from './steps/RiskIntroduction';
import { RiskConfigurationStep } from './steps/riskConfiguration/RiskConfigurationStep';
import { WorkflowIntroduction } from './steps/WorkflowIntroduction';
import { BasicSettingsStep } from './steps/BasicSettingsStep';
import { StrategyDefinitionStep } from './steps/StrategyDefinitionStep';
import { PlaybookBuilderStep, PlaybookBuilderStepRef } from './steps/playbookBuilder/PlaybookBuilderStep';
import { AccountDetailsStep } from './steps/AccountDetailsStep';
import { AccountTypeStep } from './steps/AccountTypeStep';
import { AssetsStep } from './steps/AssetsStep';
import { PropFirmStep } from './steps/PropFirmStep';
import { BrokerSelectionStep } from './steps/BrokerSelectionStep';
import { FeeConfigurationChoiceStep } from './steps/FeeConfigurationChoiceStep';
import { FeeConfigurationDetailStep } from './steps/feeConfigurationDetails/FeeConfigurationDetailStep';
import { ReviewStep } from './steps/ReviewStep';
import { CompletionSuccessModal } from './modals/CompletionSuccessModal';
import { ResumePrompt } from './components';

/**
 * Type guard to narrow SyncProviderType to ProviderKey
 * Only 'google-drive' and 'local' are valid connection providers
 */
const isValidProviderKey = (provider: string | null | undefined): provider is ProviderKey => {
  return provider === 'google-drive' || provider === 'local';
};

// ===== MILESTONE SYNC CONFIGURATION =====

/**
 * Steps that mark the end of major wizard sections
 * Sync is triggered after completing these steps for cross-device continuity
 */
const MILESTONE_STEPS = [
  'fee-config-choice',    // End of account section (standard fees path)
  'fee-config-detail',    // End of account section (custom fees path)
  'playbook-builder',     // End of trading strategy section
  'risk-configuration',   // End of risk section
  'basic-settings',       // End of workflow section
];

/**
 * Check if a step is a milestone that should trigger sync
 * Handles conditional logic for fee-config-choice (only milestone for standard fees)
 */
const isMilestone = (stepId: string, draft: OnboardingDraft): boolean => {
  // fee-config-choice is only a milestone if user chose standard fees
  // (custom fees users will sync at fee-config-detail instead)
  if (stepId === 'fee-config-choice') {
    return draft.account?.feeConfiguration?.choice === 'standard';
  }
  return MILESTONE_STEPS.includes(stepId);
};

/**
 * Trigger non-blocking sync at wizard milestones
 * Fire-and-forget pattern - errors are logged but don't interrupt wizard flow
 */
const triggerMilestoneSync = async (milestone: string): Promise<void> => {
  const syncService = BackgroundSyncService.getInstance();

  // Only sync if service is running (provider connected)
  if (!syncService.getIsRunning()) {
    return;
  }

  // Prevent overlapping sync runs
  if (syncService.getStatus() === 'syncing') {
    console.log(`⏸️ [Wizard] Skipping milestone sync (already syncing): ${milestone}`);
    return;
  }

  console.log(`🔄 [Wizard] Milestone sync: ${milestone}`);

  try {
    await syncService.syncNow();
    console.log(`✅ [Wizard] Milestone sync complete: ${milestone}`);
  } catch (error) {
    // Non-blocking - log but don't interrupt wizard flow
    console.warn(`⚠️ [Wizard] Milestone sync failed: ${milestone}`, error);
  }
};

/**
 * Perform cleanup after "Start Fresh" - sync tombstone, stop service, disconnect provider
 * Includes lazy-init: if service not running but provider exists, initialize before syncing.
 *
 * @param userId - User ID for clearing provider settings
 * @param activeSyncProvider - Current active provider from settings (avoids re-fetch)
 * @param disconnectContext - Context disconnect function to reset SyncConnectionContext state
 */
const performStartFreshCleanup = async (
  userId: string,
  activeSyncProvider: SyncProviderType | null,
  disconnectContext: () => Promise<void>
): Promise<void> => {
  const syncService = BackgroundSyncService.getInstance();

  console.log('🧹 [Wizard] Start Fresh cleanup initiated');

  try {
    // Step 1: Conditional sync attempt (push tombstone to cloud)
    let isRunning = syncService.getIsRunning();
    const isSyncing = syncService.getStatus() === 'syncing';

    // Fallback: if settings haven't loaded yet, check if service already has a provider
    // Note: createProvider() validates unknown providers gracefully (returns null + logs warning)
    const existingProvider = syncService.getProvider();
    const existingProviderName = existingProvider?.name as SyncProviderType | undefined;

    // Log mismatch if both sources provide different values (edge case debugging)
    if (activeSyncProvider && existingProviderName && activeSyncProvider !== existingProviderName) {
      console.warn(
        `⚠️ [Wizard] Start Fresh: provider mismatch - settings: ${activeSyncProvider}, service: ${existingProviderName}. Using settings value.`
      );
    }

    const effectiveProvider = activeSyncProvider ?? existingProviderName ?? null;

    // Lazy-init: if service not running but provider exists, try to initialize
    if (!isRunning && effectiveProvider && !isSyncing) {
      console.log(`🔄 [Wizard] Start Fresh: lazy-init for ${effectiveProvider}...`);

      try {
        // Create provider instance via factory
        const provider = createProvider(effectiveProvider);
        if (provider) {
          const deviceId = getOrCreateDeviceId();

          // Initialize BackgroundSyncService
          await syncService.initialize(userId, deviceId);
          syncService.setProvider(provider);

          // Attempt to start (may fail if provider not connected, etc.)
          const started = await syncService.start();
          if (started) {
            console.log('✅ [Wizard] Start Fresh: lazy-init successful, service started');
            isRunning = true;
          } else {
            console.warn('⚠️ [Wizard] Start Fresh: lazy-init failed to start service (provider may not be connected)');
          }
        } else {
          console.warn(`⚠️ [Wizard] Start Fresh: failed to create provider for ${effectiveProvider}`);
        }
      } catch (initError) {
        console.warn('⚠️ [Wizard] Start Fresh: lazy-init failed', initError);
        // Continue with cleanup even if init fails
      }
    }

    // Now attempt sync if service is running
    if (isRunning && !isSyncing) {
      console.log('🔄 [Wizard] Start Fresh: syncing tombstone to cloud...');

      // Store sync promise so we can await it after timeout
      const syncPromise = syncService.syncNow();

      // Race sync against 5s timeout for UX feedback
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<'timeout'>((resolve) => {
        timeoutId = setTimeout(() => resolve('timeout'), 5000);
      });

      try {
        const result = await Promise.race([
          syncPromise.then(() => 'success' as const),
          timeoutPromise
        ]);

        if (result === 'success') {
          if (timeoutId) clearTimeout(timeoutId);
          console.log('✅ [Wizard] Start Fresh: tombstone synced');
        } else {
          console.warn('⏱️ [Wizard] Start Fresh: sync timed out, waiting for completion...');
          // Wait for sync to settle with 30s max bound (don't block cleanup indefinitely)
          await Promise.race([
            syncPromise.catch((e) => console.warn('⚠️ [Wizard] Sync error (continuing cleanup):', e)),
            new Promise((resolve) => setTimeout(resolve, 30000))
          ]);
          console.log('✅ [Wizard] Start Fresh: sync settled (or 30s max reached)');
        }
      } catch (syncError) {
        console.warn('⚠️ [Wizard] Start Fresh: sync failed', syncError);
      }
    } else if (!isRunning && !effectiveProvider) {
      console.log('⏸️ [Wizard] Start Fresh: no provider configured, skipping sync');
    } else if (!isRunning) {
      console.log('⏸️ [Wizard] Start Fresh: sync service not running (lazy-init failed), skipping sync');
    } else {
      // Sync already in progress - wait for it to complete before cleanup
      console.log('⏳ [Wizard] Start Fresh: sync already in progress, waiting for completion...');
      const waitForIdle = async () => {
        const startTime = Date.now();
        const maxWait = 30000; // 30s max
        while (syncService.getStatus() === 'syncing' && Date.now() - startTime < maxWait) {
          await new Promise((resolve) => setTimeout(resolve, 500)); // Poll every 500ms
        }
      };
      await waitForIdle();
      console.log('✅ [Wizard] Start Fresh: in-flight sync settled (or 30s max reached)');
    }

    // Step 2: Always run cleanup (regardless of sync attempt)

    // Disconnect via context (stops sync service, disconnects provider, resets context state)
    // This ensures SyncConnectionContext state transitions to 'idle' so StorageProviderStep
    // doesn't show stale 'success' state on re-entry
    try {
      await disconnectContext();
      console.log('🔌 [Wizard] Start Fresh: context disconnected (service stopped + provider disconnected)');
    } catch (disconnectError) {
      console.warn('⚠️ [Wizard] Start Fresh: context disconnect failed', disconnectError);
    }

    // Clear provider selection in settings (Supabase + IndexedDB cache)
    // Always run even if disconnect fails or context was already idle (handles edge cases)
    await userSyncSettingsService.updateActiveSyncProvider(userId, null);
    console.log('🗑️ [Wizard] Start Fresh: provider settings cleared');

    console.log('✅ [Wizard] Start Fresh cleanup complete');
  } catch (error) {
    console.error('❌ [Wizard] Start Fresh cleanup failed:', error);
  }
};

/**
 * Main wizard container component
 */
export const OnboardingWizard: React.FC = () => {
  const { draft, loading, setCurrentScreen, markCompleted, clearReturnToScreen, clearDraft } = useOnboardingData();
  const { showToast } = useToastNotification();
  const { user } = useAuth();
  const { settings: syncSettings } = useUserSyncSettings();
  const { disconnect: disconnectContext } = useSyncConnection();
  const navigate = useNavigate();
  const scrollShadowRef = useRef<ScrollShadowRef>(null);
  const playbookBuilderRef = useRef<PlaybookBuilderStepRef>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Calculate initial step index from draft's currentScreen, with loading handling
  const getInitialStepIndex = (): number => {
    // If still loading or no draft, start at welcome (will be corrected by effect below)
    if (loading || !draft?.currentScreen) return 0;

    // We can't use static mapping because conditional steps change indexes
    // The wizard will handle this with the effect below
    return 0;
  };

  const {
    currentStepIndex,
    currentStep,
    activeSteps,
    totalSteps,
    progress,
    totalProgressSteps,
    isCurrentStepValid,
    canNavigateNext,
    canNavigateBack,
    goNext,
    goBack,
    goToStep
  } = useWizardNavigation(undefined, getInitialStepIndex());

  // Calculate current progress step (excluding non-progress steps)
  const currentProgressStep = useMemo((): number => {
    let progressStep = 0;
    for (let i = 0; i <= currentStepIndex && i < activeSteps.length; i++) {
      const step = activeSteps[i];
      if (step && !step.excludeFromProgress) {
        progressStep++;
      }
    }
    return progressStep;
  }, [activeSteps, currentStepIndex]);

  // Track previous step index for completion logic
  const prevStepIndexRef = useRef<number>(currentStepIndex);

  // Track whether user has handled the resume prompt (chose Continue or Start Fresh)
  // Prevents progress check effect from re-showing prompt after user makes a choice
  const resumePromptHandledRef = useRef<boolean>(false);

  // Track previous loading state to detect new draft loads
  const prevLoadingRef = useRef<boolean>(loading);

  // Track whether initial navigation from saved draft has occurred
  // Used to prevent sync effects from overwriting saved currentScreen before restoration
  const [hasInitialized, setHasInitialized] = useState(false);

  // Reset resume prompt handling when a new draft loads (e.g., sign-out/in, route change)
  // This ensures the prompt can appear again for a genuinely new session
  useEffect(() => {
    // Detect loading transition from true → false (new draft finished loading)
    if (prevLoadingRef.current && !loading) {
      resumePromptHandledRef.current = false;
      setHasInitialized(false);
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  // Sync currentScreen with wizard navigation
  // NOTE: Must check hasInitialized to prevent overwriting saved currentScreen on initial mount
  // (fixes race condition where this effect ran before navigation effect could restore saved screen)
  // NOTE: Also check !showResumePrompt to prevent mutating draft while user is deciding
  useEffect(() => {
    if (currentStep?.id && draft && hasInitialized && !showResumePrompt) {
      // Only update if it's different to avoid unnecessary updates
      if (draft.currentScreen !== currentStep.id) {
        setCurrentScreen(currentStep.id as typeof draft.currentScreen);
      }
    }
  }, [currentStep?.id, draft, setCurrentScreen, hasInitialized, showResumePrompt]);

  // Mark completed steps when moving forward and trigger milestone sync
  useEffect(() => {
    const prevStepIndex = prevStepIndexRef.current;

    // If we moved forward (not backward), mark the previous step as completed
    if (currentStepIndex > prevStepIndex && prevStepIndex < activeSteps.length) {
      const prevStep = activeSteps[prevStepIndex];
      if (prevStep?.id) {
        // Mark all steps as completed when navigating forward
        // Intro screens mark themselves complete via useEffect
        // Regular screens get marked complete here when user clicks Next
        markCompleted(prevStep.id as Parameters<typeof markCompleted>[0]);

        // Trigger milestone sync for cross-device continuity (non-blocking)
        if (draft && isMilestone(prevStep.id, draft)) {
          void triggerMilestoneSync(prevStep.id);
        }
      }
    }

    // Update the ref for next comparison
    prevStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex, activeSteps, markCompleted, draft]);

  // Once draft loads, check for progress and either show resume prompt or navigate to saved screen
  useEffect(() => {
    if (!loading && draft && activeSteps.length > 0 && !hasInitialized && !showResumePrompt && !resumePromptHandledRef.current) {
      // Check if user has progress beyond welcome screen
      const hasProgress = (draft.currentScreen && draft.currentScreen !== 'welcome')
        || (draft.completedScreens && draft.completedScreens.length > 0);

      if (hasProgress) {
        // Show resume prompt instead of auto-navigating
        setShowResumePrompt(true);
        return;
      }

      // No progress - initialize normally (stay on welcome)
      setHasInitialized(true);
    }

    // Return undefined for code paths that don't set a timer
    return undefined;
  }, [loading, draft, activeSteps.length, hasInitialized, showResumePrompt]);

  // Handle navigation after user chooses to continue (resume prompt dismissed)
  useEffect(() => {
    if (!loading && draft?.currentScreen && activeSteps.length > 0 && !hasInitialized && !showResumePrompt) {
      // Add delay to let data updates and conditional step logic fully settle before navigation
      const timer = setTimeout(() => {
        // Find the correct step index based on activeSteps (accounts for conditional steps)
        const targetStepIndex = activeSteps.findIndex(step => step.id === draft.currentScreen);

        if (targetStepIndex !== -1 && targetStepIndex !== currentStepIndex) {
          goToStep(targetStepIndex);
        }

        setHasInitialized(true);
      }, 100); // Increased delay to allow fee configuration state to stabilize

      return () => clearTimeout(timer);
    }

    // Return undefined for code paths that don't set a timer
    return undefined;
  }, [loading, draft?.currentScreen, activeSteps, goToStep, hasInitialized, currentStepIndex, showResumePrompt]);

  // Validate currentStepIndex when activeSteps changes (e.g., conditional steps appear/disappear)
  useEffect(() => {
    // If current index is out of bounds due to activeSteps changing, reset to last valid step
    if (currentStepIndex >= activeSteps.length && activeSteps.length > 0) {
      const newIndex = Math.max(0, activeSteps.length - 1);
      goToStep(newIndex);
    }
  }, [activeSteps.length, currentStepIndex, goToStep]);

  // Scroll to top of ScrollShadow when step changes
  useEffect(() => {
    if (scrollShadowRef.current) {
      scrollShadowRef.current.scrollToTop('instant');
    }
  }, [currentStepIndex]);

  // Initial scroll to top on mount
  useEffect(() => {
    if (scrollShadowRef.current) {
      scrollShadowRef.current.scrollToTop('instant');
    }
  }, []);

  /**
   * Handle user choosing to continue from resume prompt
   * Navigates directly to saved screen, then dismisses prompt to avoid welcome flash
   */
  const handleResumeContinue = (): void => {
    // Mark as handled BEFORE setting state to prevent progress check effect from re-triggering
    resumePromptHandledRef.current = true;

    // Navigate directly to saved screen (avoids welcome flash from delayed effect)
    if (draft?.currentScreen && activeSteps.length > 0) {
      const targetStepIndex = activeSteps.findIndex(step => step.id === draft.currentScreen);
      if (targetStepIndex !== -1 && targetStepIndex !== currentStepIndex) {
        goToStep(targetStepIndex);
      }
    }

    // Mark as initialized since we handled navigation here
    setHasInitialized(true);
    setShowResumePrompt(false);
  };

  /**
   * Handle user choosing to start fresh from resume prompt
   * Clears draft (writes tombstone for sync-aware deletion) and resets to welcome screen
   * Awaits cleanup with loading state: sync tombstone, stop service, disconnect provider
   *
   * Ordering (per GPT feedback):
   * 1. BEFORE await: resumePromptHandledRef, clearDraft, setHasInitialized (synchronous setup)
   * 2. Loading state + toast suppression
   * 3. Await cleanup
   * 4. AFTER cleanup: navigate to welcome, dismiss prompt
   */
  const handleResumeStartFresh = async (): Promise<void> => {
    // FIRST (before await): Synchronous setup to prevent race conditions
    // Mark as handled to prevent progress check effect from re-triggering
    resumePromptHandledRef.current = true;
    // Clear draft (writes tombstone) - must exist BEFORE sync attempt
    clearDraft();
    // Prevent navigation effects from using stale currentScreen data
    setHasInitialized(true);

    // Show loading state on ResumePrompt
    setIsCleaningUp(true);

    // Guard: skip cleanup if no user ID
    if (!user?.id) {
      console.warn('⚠️ [Wizard] Start Fresh: no user ID, skipping provider cleanup');
      goToStep(0);
      setShowResumePrompt(false);
      setIsCleaningUp(false);
      return;
    }

    // Suppress toasts during cleanup (fail-safe: clear in finally)
    userSyncSettingsService.setSuppressDisconnectToast(true);
    userSyncSettingsService.setSuppressSettingsToast(true);

    try {
      // Await cleanup (sync tombstone, stop service, disconnect provider, clear settings)
      // Pass activeSyncProvider to enable lazy-init if service not running
      // Pass disconnectContext to reset SyncConnectionContext state (prevents stale UI)
      await performStartFreshCleanup(user.id, syncSettings?.activeSyncProvider ?? null, disconnectContext);
    } finally {
      // Always clear loading state and navigate
      // (navigation in finally ensures user isn't stuck on prompt if cleanup throws)
      setIsCleaningUp(false);
      goToStep(0);
      setShowResumePrompt(false);

      // Delay clearing suppression flags to account for async Supabase realtime echo
      // (the settings update we just made will arrive via realtime subscription shortly)
      setTimeout(() => {
        userSyncSettingsService.setSuppressDisconnectToast(false);
        userSyncSettingsService.setSuppressSettingsToast(false);
      }, 500);
    }
  };

  /**
   * Handle form submission (final step)
   */
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!draft) {
      console.error('No draft data available for submission');
      return;
    }

    if (!user) {
      console.error('No authenticated user found');
      setSaveError('You must be logged in to complete onboarding');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Step 1: Persist onboarding data to IndexedDB
      console.log('📝 Persisting onboarding data to IndexedDB...');
      const persistResult = await persistOnboardingData(draft);

      if (!persistResult.success) {
        setSaveError(persistResult.error || 'Failed to save your setup to local storage');
        return;
      }

      console.log('✅ Onboarding data persisted to IndexedDB');

      // Step 1.5: Final sync before completion (with 3s timeout to avoid blocking)
      const syncService = BackgroundSyncService.getInstance();
      if (syncService.getIsRunning() && syncService.getStatus() !== 'syncing') {
        console.log('🔄 [Wizard] Final sync before completion...');
        try {
          const syncTimeout = new Promise<void>((resolve) => setTimeout(resolve, 3000));
          await Promise.race([
            syncService.syncNow().then(() => {
              console.log('✅ [Wizard] Final sync complete');
            }),
            syncTimeout.then(() => {
              console.log('⏱️ [Wizard] Final sync timeout (3s) - continuing to completion');
            })
          ]);
        } catch (syncError) {
          // Non-blocking - log but continue with completion
          console.warn('⚠️ [Wizard] Final sync failed (non-blocking):', syncError);
        }
      }

      // Step 2: Update Supabase user record with onboarding completion
      // Note: This is non-blocking - if Supabase fails, we still proceed since IndexedDB save succeeded
      console.log('☁️ Updating Supabase with onboarding completion...');
      try {
        const updatedUser = await userService.completeOnboarding(user.id);
        if (updatedUser) {
          console.log('✅ Onboarding completion saved to Supabase');
        } else {
          // Supabase returned null - log warning but continue (IndexedDB save already succeeded)
          console.warn('⚠️ [Wizard] Supabase onboarding update returned null', { userId: user.id });
        }
      } catch (supabaseError) {
        // Supabase failed - log structured warning but continue (IndexedDB save already succeeded)
        console.warn('⚠️ [Wizard] Supabase onboarding update failed (non-blocking)', {
          userId: user.id,
          error: supabaseError instanceof Error ? supabaseError.message : String(supabaseError)
        });
      }

      // Step 3: Clear IndexedDB wizard draft (idempotent - ok if already cleared)
      console.log('🧹 Clearing onboarding draft from IndexedDB...');
      try {
        await softDeleteRecord('wizard_drafts', `${Brand.cachePrefix}-onboarding-draft`);
        console.log('✅ Draft cleared');
      } catch (deleteError) {
        // Treat "not found" as success (draft may have been cleared in another tab)
        const isNotFoundError =
          deleteError instanceof Error &&
          deleteError.message.startsWith('[softDeleteRecord] Record not found:');
        if (isNotFoundError) {
          console.log('ℹ️ Draft already cleared (not found)');
        } else {
          // Re-throw unexpected errors
          throw deleteError;
        }
      }

      // Step 4: Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('❌ Unexpected error during onboarding submission:', error);
      setSaveError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle navigation to dashboard after success
   */
  const handleGoToDashboard = (): void => {
    console.log('🚀 Navigating to dashboard...');
    navigate('/dashboard');
  };

  // Show loading state if data is still loading (after all hooks are called)
  if (loading || !draft) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  /**
   * Render the current step component (or resume prompt if showing)
   */
  const renderCurrentStep = (): React.JSX.Element => {
    // Show resume prompt for returning users with progress
    if (showResumePrompt) {
      // Guard against transient undefined during initial load
      const lastScreenName = activeSteps.length > 0
        ? activeSteps.find(s => s.id === draft.currentScreen)?.name
        : undefined;

      return (
        <div className="h-full flex items-center justify-center">
          <ResumePrompt
            onContinue={handleResumeContinue}
            onStartFresh={handleResumeStartFresh}
            lastScreenName={lastScreenName}
            isLoading={isCleaningUp}
          />
        </div>
      );
    }

    switch (currentStep.id) {
      case 'welcome':
        return <WelcomeStep goToNextStep={() => goToStep(currentStepIndex + 1)} />;
      case 'storage-provider':
        return (
          <StorageProviderStep
            goToNextStep={() => goToStep(currentStepIndex + 1)}
            onSkip={() => goToStep(currentStepIndex + 1)}
          />
        );
      case 'account-intro':
        return <AccountSetupIntroduction goToNextStep={() => goToStep(currentStepIndex + 1)} goToPreviousStep={() => goToStep(currentStepIndex - 1)} />;
      case 'strategy-intro':
        return <StrategyIntroduction goToNextStep={() => goToStep(currentStepIndex + 1)} goToPreviousStep={() => goToStep(currentStepIndex - 1)} />;
      case 'strategy-definition':
        return <StrategyDefinitionStep />;
      case 'playbook-builder':
        return <PlaybookBuilderStep ref={playbookBuilderRef} />;
      case 'risk-intro':
        return <RiskIntroduction goToNextStep={() => goToStep(currentStepIndex + 1)} goToPreviousStep={() => goToStep(currentStepIndex - 1)} />;
      case 'risk-configuration':
        return <RiskConfigurationStep />;
      case 'workflow-intro':
        return <WorkflowIntroduction />;
      case 'basic-settings':
        return <BasicSettingsStep />;
      case 'account-details':
        return <AccountDetailsStep />;
      case 'account-type':
        return <AccountTypeStep />;
      case 'assets':
        return <AssetsStep />;
      case 'prop-firm':
        return <PropFirmStep />;
      case 'broker':
        return <BrokerSelectionStep />;
      case 'fee-config-choice':
        return <FeeConfigurationChoiceStep />;
      case 'fee-config-detail':
        return <FeeConfigurationDetailStep />;
      case 'review':
        return <ReviewStep activeSteps={activeSteps} goToStep={goToStep} />;
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              {currentStep.title}
            </h2>
            <p className="text-text-secondary">
              Step {currentStepIndex + 1} of {totalSteps} - Coming Soon
            </p>
          </div>
        );
    }
  };


  if (!draft) {
    return (
      <WizardLayout
        currentStep="1"
        totalSteps={totalSteps}
        progress={0}
        stepName="Loading..."
        showHeader={false}
      >
        <div className="min-h-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading your trading journal setup...</p>
          </div>
        </div>
      </WizardLayout>
    );
  }

  /**
   * Helper: Find the index of the review step in activeSteps
   */
  const getReviewStepIndex = (): number => {
    return activeSteps.findIndex(step => step.id === 'review');
  };

  /**
   * Helper: Check if there are incomplete conditional steps between current and return screen
   * Returns array of step IDs that need to be completed before returning
   */
  const findIncompleteConditionalSteps = (): string[] => {
    if (!draft?.returnToScreen || draft.returnToScreen !== 'review') return [];

    const reviewIndex = getReviewStepIndex();
    if (reviewIndex === -1) return [];

    const incompleteSteps: string[] = [];

    // Check from current to review for conditional steps that need completion
    for (let i = currentStepIndex + 1; i < reviewIndex; i++) {
      const step = activeSteps[i];
      if (!step) continue;

      // Check if this is a conditional step that appeared due to data changes
      // Prop firm step is conditional on accountType === 'prop'
      if (step.id === 'prop-firm' && draft.account?.accountType === 'prop') {
        // Check if prop firm data is complete
        const propDetails = draft.account.propFirmDetails;
        if (!propDetails?.firmName || !propDetails?.accountStage) {
          incompleteSteps.push(step.id);
        }
      }

      // Fee config detail step is conditional on custom fees or custom broker
      if (step.id === 'fee-config-detail') {
        const feeConfig = draft.account?.feeConfiguration;
        if (feeConfig?.choice === 'custom' || feeConfig?.isCustomBroker) {
          // Check if fee configuration is complete
          if (!feeConfig.configurationComplete) {
            incompleteSteps.push(step.id);
          }
        }
      }
    }

    return incompleteSteps;
  };

  /**
   * Helper: Get the appropriate Next button label based on return-to-screen state
   */
  const getNextButtonLabel = (): string => {
    if (isSaving) return 'Saving...';
    if (isLastStep) return 'Complete Setup';
    if (!draft?.returnToScreen) return 'Next';

    const incompleteConditionals = findIncompleteConditionalSteps();
    if (incompleteConditionals.length > 0) {
      return 'Save & Continue';
    }

    // Check if we're in the middle of account section flow (not at the end yet)
    const accountSectionSteps = ['account-details', 'account-type', 'assets', 'prop-firm'];
    if (accountSectionSteps.includes(currentStep.id)) {
      // If we're on assets or prop-firm and have changes, we'll continue to broker
      if (currentStep.id === 'assets' || currentStep.id === 'prop-firm') {
        const snapshot = draft?.editSnapshot;
        if (snapshot) {
          const accountTypeChanged = snapshot.accountType !== draft.account?.accountType;
          const assetsChanged = JSON.stringify(snapshot.selectedAssetClasses) !==
                               JSON.stringify(draft.account?.selectedAssetClasses);

          if (accountTypeChanged || assetsChanged) {
            return 'Next';
          }
        }
      } else {
        // On account-details or account-type, always continue
        return 'Next';
      }
    }

    // Check if we're in broker/fees flow and will continue
    if (currentStep.id === 'broker') {
      return 'Next';
    }

    // Check if we're on playbook-builder and playbooks changed (will continue to risk-configuration)
    if (currentStep.id === 'playbook-builder') {
      const snapshot = draft?.editSnapshot;
      if (snapshot && snapshot.playbookTempIds !== undefined) {
        const currentPlaybookIds = draft.trading?.playbooks?.map(p => p.tempId) || [];
        const snapshotPlaybookIds = snapshot.playbookTempIds || [];

        const playbooksChanged =
          currentPlaybookIds.length !== snapshotPlaybookIds.length ||
          !currentPlaybookIds.every(id => snapshotPlaybookIds.includes(id)) ||
          !snapshotPlaybookIds.every(id => currentPlaybookIds.includes(id));

        if (playbooksChanged) {
          return 'Next';
        }
      }
    }

    return 'Save & Return to Review';
  };

  /**
   * Helper: Get validation message for current step
   */
  const getValidationMessage = (): string | undefined => {
    switch (currentStep.id) {
      case 'account-details':
        return 'Please enter an account name';
      case 'account-type':
        return 'Please select an account type (Personal, Prop Firm, or Paper)';
      case 'assets':
        return 'Please select at least one asset class to trade';
      case 'prop-firm':
        return 'Please select your prop firm and account stage';
      case 'broker':
        return 'Please select your trading broker';
      case 'fee-config-choice':
        return 'Please choose between standard or custom fees';
      case 'fee-config-detail':
        return 'Please configure your fee structure';
      case 'strategy-definition':
        return 'Please enter a strategy name';
      case 'playbook-builder': {
        // More detailed validation for playbooks
        const playbooks = draft?.trading?.playbooks;
        if (!playbooks || playbooks.length === 0) {
          return 'Please create at least one playbook';
        }
        // Check if any playbook is missing entry models
        const hasPlaybookWithoutEntry = playbooks.some(p => !p.entryModelIds || p.entryModelIds.length === 0);
        if (hasPlaybookWithoutEntry) {
          return 'Each playbook must have at least one entry model';
        }
        return 'Please complete your playbook configuration';
      }
      case 'risk-configuration':
        return 'Please configure at least one risk profile';
      case 'basic-settings':
        return 'Please configure timezone, time format, and week start';
      case 'review':
        return 'Please complete all required sections';
      default:
        return 'Please complete this step to continue';
    }
  };

  /**
   * Helper: Handle Next button click with validation and edit-and-return logic
   */
  const handleNextClick = (): void => {
    // Check validation first - if invalid, show toast and don't proceed
    const isValid = isLastStep ? isCurrentStepValid : canNavigateNext;
    if (!isValid) {
      const validationMsg = getValidationMessage();
      showToast({
        title: 'Required',
        description: validationMsg || 'Please complete this step to continue',
        color: 'warning'
      });

      // Trigger visual feedback for playbook builder
      if (currentStep.id === 'playbook-builder') {
        playbookBuilderRef.current?.flashInvalidPlaybooks();
      }

      return;
    }

    if (isLastStep) {
      const event = new Event('submit') as unknown as React.FormEvent;
      handleSubmit(event);
      return;
    }

    // Check for edit-and-return flow
    if (draft?.returnToScreen === 'review') {
      const incompleteConditionals = findIncompleteConditionalSteps();
      if (incompleteConditionals.length > 0) {
        // Navigate to next incomplete conditional step
        const nextStepId = incompleteConditionals[0];
        const nextStepIndex = activeSteps.findIndex(s => s.id === nextStepId);
        if (nextStepIndex !== -1) {
          goToStep(nextStepIndex);
          return;
        }
      }

      // Check if we just completed the account section and need to review broker
      // This happens after assets or prop-firm (whichever is the last account step)
      if (currentStep.id === 'assets' || currentStep.id === 'prop-firm') {
        const snapshot = draft?.editSnapshot;
        if (snapshot) {
          const accountTypeChanged = snapshot.accountType !== draft.account?.accountType;
          const assetsChanged = JSON.stringify(snapshot.selectedAssetClasses) !==
                               JSON.stringify(draft.account?.selectedAssetClasses);

          if (accountTypeChanged || assetsChanged) {
            // Navigate to broker to review selection
            const brokerIndex = activeSteps.findIndex(s => s.id === 'broker');
            if (brokerIndex !== -1) {
              goToStep(brokerIndex);
              return;
            }
          }
        }
      }

      // Check if we just completed playbook-builder and need to review risk configuration
      // This happens if playbooks were added or removed
      if (currentStep.id === 'playbook-builder') {
        const snapshot = draft?.editSnapshot;
        if (snapshot) {
          const currentPlaybookIds = draft.trading?.playbooks?.map(p => p.tempId) || [];
          const snapshotPlaybookIds = snapshot.playbookTempIds || [];

          // Compare playbook IDs (order doesn't matter)
          const playbooksChanged =
            currentPlaybookIds.length !== snapshotPlaybookIds.length ||
            !currentPlaybookIds.every(id => snapshotPlaybookIds.includes(id)) ||
            !snapshotPlaybookIds.every(id => currentPlaybookIds.includes(id));

          if (playbooksChanged) {
            // Navigate to risk-configuration to review profiles
            const riskIndex = activeSteps.findIndex(s => s.id === 'risk-configuration');
            if (riskIndex !== -1) {
              goToStep(riskIndex);
              return;
            }
          }
        }
      }

      // No incomplete conditionals - check if current step is the last in its section
      const reviewIndex = getReviewStepIndex();

      // Define section boundaries - these steps should return to review after completion
      // Account Details section: account-details → account-type → assets → (prop-firm if needed) → [broker if changed] → fee-config-choice → review
      // Broker/Fees section: broker → fee-config-choice → (fee-config-detail if needed) → review
      // Other sections: single step then return
      const sectionEndSteps = [
        'assets',                 // End of account section (if no changes to type/assets)
        'prop-firm',             // End of account section for prop accounts (if no changes to type/assets)
        'fee-config-choice',      // End of account/broker/fees section
        'fee-config-detail',      // End of account/broker/fees section (if custom)
        'strategy-definition',    // Strategy section
        'playbook-builder',       // Playbooks section
        'risk-configuration',     // Risk section
        'basic-settings'          // Settings section
      ];

      // If current step is a section end and valid, return to review
      if (sectionEndSteps.includes(currentStep.id) && isCurrentStepValid) {
        clearReturnToScreen();
        goToStep(reviewIndex);
        return;
      }

      // Otherwise, continue to next step in sequence
      goNext();
      return;
    }

    // Normal flow - just go to next step
    goNext();
  };

  // Prepare standardized navigation configuration
  // Educational screens always hide footer - navigation is in the cards themselves
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Check if step is valid for visual button styling
  const isStepValid = isLastStep ? isCurrentStepValid : canNavigateNext;

  // Navigation configuration varies by step type:
  // - Resume prompt: mode 'none' (action buttons in ResumePrompt handle navigation)
  // - Educational intros: mode 'none' (navigation in cards)
  // - Storage provider: back-only (action buttons handle advancement)
  // - All others: standard next/back navigation
  const navigation = showResumePrompt || currentStep.id === 'welcome' || currentStep.id === 'account-intro' || currentStep.id === 'strategy-intro'|| currentStep.id === 'risk-intro' ? {
    mode: 'none' as const
  } : currentStep.id === 'storage-provider' ? {
    // Storage provider step: Back button only, action buttons advance wizard
    mode: 'navigation' as const,
    ...(canNavigateBack && {
      backButton: {
        label: 'Back',
        onClick: goBack,
        disabled: isSaving
      }
    })
  } : {
    mode: 'navigation' as const,
    nextButton: {
      label: getNextButtonLabel(),
      onClick: handleNextClick,
      // Keep button enabled but change appearance based on validation
      disabled: isSaving,
      color: isStepValid && !isSaving ? ('primary' as const) : ('default' as const),
      variant: isStepValid && !isSaving ? ('solid' as const) : ('bordered' as const),
      isLoading: isSaving
    },
    ...(canNavigateBack && {
      backButton: {
        label: 'Back',
        onClick: goBack,
        disabled: isSaving
      }
    })
  };

  // Derive sync status content for header
  // Only show after user has navigated past the storage-provider step in the current wizard flow
  const storageProviderStepIndex = activeSteps.findIndex(step => step.id === 'storage-provider');
  const isPastStorageStep = storageProviderStepIndex !== -1 && currentStepIndex > storageProviderStepIndex;
  const activeSyncProvider = syncSettings?.activeSyncProvider;
  const syncStatusContent = isPastStorageStep && isValidProviderKey(activeSyncProvider)
    ? <AppSyncStatus variant="wizard" providerKey={activeSyncProvider} />
    : undefined;

  // All steps use the same container card layout
  return (
    <WizardLayout
      currentStep={showResumePrompt ? '' : `${currentStepIndex + 1}`}
      totalSteps={totalSteps}
      progress={showResumePrompt ? 0 : progress}
      stepName={showResumePrompt ? 'Welcome Back' : currentStep.name}
      showHeader={true}
      navigation={navigation}
      scrollRef={scrollShadowRef}
      currentProgressStep={showResumePrompt ? 0 : currentProgressStep}
      totalProgressSteps={totalProgressSteps}
      excludeCurrentFromProgress={showResumePrompt || !!currentStep.excludeFromProgress}
      syncStatusContent={showResumePrompt ? undefined : syncStatusContent}
      headerActions={<SupportButton />}
    >
      {/* Error Display */}
      {saveError && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg">
          <p className="text-danger-700 font-medium">Error saving your setup</p>
          <p className="text-danger-600 text-sm mt-1">{saveError}</p>
        </div>
      )}

      {/* Step Content Only - Navigation moved to footer */}
      {renderCurrentStep()}

      {/* Success Modal */}
      <CompletionSuccessModal
        isOpen={showSuccessModal}
        onGoToDashboard={handleGoToDashboard}
      />
    </WizardLayout>
  );
};